import {GET_SAMPLE_REFERENCES, CLEAR_SAMPLE_REFERENCES, CREATE_ENTITY_FORM, CREATE_SAMPLE_REFERENCE, UPDATE_SAMPLE_REFERENCE} from '../../actions/actiontypes';
import initialstate from '../initialstate';
import {isApiResponseOk} from '../../util/utils';

/**
 * Reducer for sampleReferences state.
 * The state shape is always normalized to an object of the form
 * `{ sampleReferences: [...] }` so consumers get a stable shape across renders.
 * @param {Object} state - Current state
 * @param {Object} action - Redux action
 * @returns {Object} New state
 */
export function sampleReferencesReducer(state = initialstate.sampleReferences, action) {
	switch (action.type) {
		case GET_SAMPLE_REFERENCES: {
			if (!isApiResponseOk(action)) {
				return state;
			}

			const response = action.response;

			// The API returns an array of sample reference objects. Rebuild from the initial slice
			// shape so legacy/extra keys can't drift into the state.
			if (Array.isArray(response)) {
				return {...initialstate.sampleReferences, sampleReferences: response};
			}

			// Defensive: if the response is already an object exposing a sampleReferences array, use it.
			if (response && Array.isArray(response.sampleReferences)) {
				return {...initialstate.sampleReferences, sampleReferences: response.sampleReferences};
			}

			return state;
		}
		case CLEAR_SAMPLE_REFERENCES:
			return initialstate.sampleReferences;
		case CREATE_SAMPLE_REFERENCE:
		case UPDATE_SAMPLE_REFERENCE: {
			if (!isApiResponseOk(action)) {
				return state;
			}

			const response = action.response;

			if (!response) {
				return state;
			}

			// sampleReferenceDisplayOrders array (contains only items whose display order changed)
			const displayOrders = response.sampleReferenceDisplayOrders || [];
			const newSampleReference = response;

			// for edit case get the existing sample reference ID and update it in the list instead of adding a new one
			const existingIndex = (state.sampleReferences ?? []).findIndex((item) => item.sampleReferenceId === newSampleReference.sampleReferenceId);
			let updatedList;
			if (existingIndex !== -1) {
				updatedList = [...(state.sampleReferences ?? [])];
				updatedList[existingIndex] = newSampleReference;
			} else {
				// Add the new sample reference to the existing list
				updatedList = [...(state.sampleReferences ?? []), newSampleReference];
			}

			// Update uiDisplayOrder for items that appear in displayOrders (items affected by the insert)
			const finalList = updatedList.map((item) => {
				const orderInfo = displayOrders.find((order) => order.sampleReferenceID === item.sampleReferenceId);
				// If this item's display order changed, update it; otherwise keep it as-is
				return orderInfo ? {...item, uiDisplayOrder: orderInfo.newUIDisplayOrder} : item;
			});

			// Sort by uiDisplayOrder to maintain proper display sequence
			finalList.sort((a, b) => (a.uiDisplayOrder ?? 0) - (b.uiDisplayOrder ?? 0));

			return {
				...state,
				sampleReferences: finalList
			};
		}
		case CREATE_ENTITY_FORM: {
			if (!isApiResponseOk(action)) {
				return state;
			}

			const createdDocument = Array.isArray(action.response) ? action.response[0] : action.response;

			// entityObjectId maps back to the sampleReferenceId that was passed to the EntityForms call.
			const sampleReferenceId = createdDocument?.entityObjectId ?? action.objectIds?.[0];

			if (!createdDocument || sampleReferenceId === undefined || sampleReferenceId === null) {
				return state;
			}

			const relatedDocument = {
				sampleReferenceId,
				documentId: createdDocument.documentID ?? createdDocument.documentId,
				documentName: createdDocument.documentName,
				fileExt: 'smt',
				documentTypeId: action.documentTypeId
			};

			return {
				...state,
				sampleReferences: (state.sampleReferences ?? []).map((sampleReference) => {
					if (sampleReference.sampleReferenceId !== sampleReferenceId) {
						return sampleReference;
					}

					return {
						...sampleReference,
						relatedDocuments: [...(sampleReference.relatedDocuments ?? []), relatedDocument]
					};
				})
			};
		}
		default:
			return state;
	}
}
